import { useState } from 'react'
import { Icon } from '@iconify/react'
import { toast } from 'react-toastify'
import Button from '../common/Button'
import LoadingSpinner from '../common/LoadingSpinner'
import { complianceService } from '../../services/api/complianceService'
import {
  SEVERITY_BAR_CLASSES,
  SEVERITY_COLORS,
  formatReportDate,
  formatScoreChange,
  sampleTrendPoints,
  scoreColor,
  scoreTextClass,
} from '../../utils/complianceReportHelpers'

const InlineState = ({ icon, title, message, actionLabel, onAction }) => (
  <div className="text-center py-12 px-4 rounded-xl border border-dashed border-border-light bg-gray-50">
    <Icon icon={icon} className="text-4xl mx-auto mb-3 text-gray-400" />
    <p className="text-sm font-semibold text-text-dark">{title}</p>
    <p className="text-sm text-text-secondary mt-1">{message}</p>
    {actionLabel && (
      <button
        type="button"
        onClick={onAction}
        className="mt-4 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary-hover transition-colors"
      >
        <Icon icon="mdi:refresh" />
        {actionLabel}
      </button>
    )}
  </div>
)

const SummaryCard = ({ label, value, valueClass = 'text-text-dark', sub, subClass = 'text-gray-500' }) => (
  <div className="rounded-lg border border-border-light bg-[#F9FAFB] p-3 md:p-4">
    <p className="text-[11px] text-gray-500 mb-1">{label}</p>
    <p className={`text-xl md:text-2xl font-bold ${valueClass}`}>{value}</p>
    {sub && <p className={`text-[11px] mt-1 ${subClass}`}>{sub}</p>}
  </div>
)

const ComplianceReportSection = ({ report, websiteId, isLoading = false, error = null, onRetry }) => {
  const [isExporting, setIsExporting] = useState(false)

  const summary = report?.summary ?? {}
  const website = report?.website ?? {}
  const violationsBySeverity = report?.violationsBySeverity ?? []
  const outstandingByCategory = report?.outstandingByCategory ?? []
  const scoreTrend = report?.scoreTrend ?? {}
  const topViolationTypes = report?.topViolationTypes ?? []
  const recentRemediation = report?.recentRemediation ?? []

  const currentScore = summary.currentScore ?? 0
  const scoreChange = summary.scoreChange ?? 0
  const violationsFound = summary.violationsFound ?? 0
  const violationsFixed = summary.violationsFixed ?? 0
  const totalScans = summary.totalScans ?? 0
  const fixRate = summary.fixRate ?? 0
  const outstandingViolations = summary.outstandingViolations ?? 0

  const trendBars = sampleTrendPoints(scoreTrend.points ?? [], 20)
  const maxCategoryCount = Math.max(1, ...outstandingByCategory.map((c) => c.count))

  const handleExportPdf = async () => {
    if (!websiteId) return
    try {
      setIsExporting(true)
      const blob = await complianceService.downloadComplianceReportPdf(websiteId)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `compliance-report-${websiteId}.pdf`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
      toast.success('Compliance report PDF downloaded successfully')
    } catch {
      toast.error('Failed to download compliance report PDF')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div>
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-lg font-semibold text-text-dark">Exportable Compliance Report</h2>
          <p className="text-sm text-text-secondary mt-0.5">
            Preview matches the downloadable PDF — ready for your attorney or demand letter response.
          </p>
        </div>
        <Button
          icon="mdi:file-pdf-box"
          onClick={handleExportPdf}
          disabled={isExporting || isLoading || !websiteId || !report}
          className="whitespace-nowrap shrink-0"
        >
          {isExporting ? 'Generating...' : 'Export PDF Report'}
        </Button>
      </div>

      {error && (
        <InlineState
          icon="mdi:alert-circle-outline"
          title="Compliance report could not be loaded"
          message="We could not prepare the report preview right now. Please try again."
          actionLabel="Retry"
          onAction={onRetry}
        />
      )}

      {isLoading && (
        <div className="py-16 rounded-xl border border-border-light bg-white">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {!isLoading && !error && report && (
        <div className="bg-white rounded-2xl border border-border-light shadow-lg overflow-hidden">
          {/* PDF-style header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-5 py-6 md:px-8 md:py-8">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-5 h-5 rounded-full bg-white/25 flex items-center justify-center">
                    <Icon icon="mdi:check" className="text-xs" />
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-100">
                    SentryPrime Compliance Report
                  </span>
                </div>
                <h3 className="text-xl md:text-2xl font-bold truncate">{website.name ?? 'Website'}</h3>
                <p className="text-sm text-indigo-100 mt-1 truncate">
                  {website.domain ?? website.url ?? ''}
                  {website.platform ? ` · ${website.platform}` : ''}
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-3xl md:text-4xl font-bold">{currentScore}%</p>
                <p className="text-xs text-indigo-100 mt-0.5">Compliance Score</p>
              </div>
            </div>
          </div>

          <div className="p-5 md:p-8 space-y-8">
            {/* Summary cards — mirrors PDF row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <SummaryCard
                label="Current Score"
                value={`${currentScore}%`}
                sub={formatScoreChange(scoreChange)}
                subClass="text-green-600 font-medium"
              />
              <SummaryCard
                label="Violations Found"
                value={violationsFound.toLocaleString()}
                sub={`Across ${totalScans.toLocaleString()} scans`}
              />
              <SummaryCard
                label="Violations Fixed"
                value={violationsFixed.toLocaleString()}
                valueClass="text-green-600"
                sub={`${fixRate}% fix rate`}
                subClass="text-green-600 font-medium"
              />
              <SummaryCard
                label="Outstanding Violations"
                value={outstandingViolations.toLocaleString()}
                valueClass="text-orange-600"
                sub="Still open across all scans"
                subClass="text-orange-600"
              />
            </div>

            {/* Two-column charts */}
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-sm font-bold text-indigo-600 mb-4">Violations: Found vs. Fixed</h4>
                <div className="space-y-3">
                  {violationsBySeverity.map((row) => {
                    const barClass = SEVERITY_BAR_CLASSES[row.label] ?? 'bg-indigo-500'
                    const ratio = row.found > 0 ? row.fixed / row.found : 0
                    return (
                      <div key={row.label} className="flex items-center gap-3">
                        <span className="text-xs text-gray-500 w-14 shrink-0">{row.label}</span>
                        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${barClass}`}
                            style={{ width: `${Math.max(ratio * 100, ratio > 0 ? 4 : 0)}%` }}
                          />
                        </div>
                        <span className="text-xs font-bold text-text-dark w-14 text-right shrink-0">
                          {row.fixed}/{row.found}
                        </span>
                      </div>
                    )
                  })}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-bold text-indigo-600 mb-4">Outstanding by Category</h4>
                <div className="space-y-3">
                  {outstandingByCategory.map((row) => {
                    const ratio = row.count / maxCategoryCount
                    return (
                      <div key={row.label} className="flex items-center gap-3">
                        <span className="text-xs text-gray-500 w-28 shrink-0 truncate" title={row.label}>
                          {row.label}
                        </span>
                        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full bg-indigo-600"
                            style={{ width: `${Math.max(ratio * 100, ratio > 0 ? 4 : 0)}%` }}
                          />
                        </div>
                        <span className="text-xs font-bold text-text-dark w-8 text-right shrink-0">
                          {row.count}
                        </span>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>

            {/* Score trend */}
            {trendBars.length > 0 && (
              <div>
                <h4 className="text-sm font-bold text-indigo-600 mb-4">Compliance Score Trend</h4>
                <div className="flex items-end gap-1 h-20 mb-2">
                  {trendBars.map((point, i) => {
                    const height = Math.max(4, (point.score / 100) * 100)
                    const color = scoreColor(point.score)
                    return (
                      <div
                        key={`${point.date}-${i}`}
                        className="flex-1 rounded-sm min-w-0"
                        style={{ height: `${height}%`, backgroundColor: color }}
                        title={`${formatReportDate(point.date)}: ${point.score}%`}
                      />
                    )
                  })}
                </div>
                <div className="flex justify-between text-[11px] text-gray-500 mb-3">
                  <span>
                    First scan: {formatReportDate(scoreTrend.firstScan?.date)} ·{' '}
                    <span className={scoreTextClass(scoreTrend.firstScan?.score)}>
                      {scoreTrend.firstScan?.score}%
                    </span>
                  </span>
                  <span className="text-right">
                    Latest scan: {formatReportDate(scoreTrend.latestScan?.date)} ·{' '}
                    <span className={scoreTextClass(scoreTrend.latestScan?.score)}>
                      {scoreTrend.latestScan?.score}%
                    </span>
                  </span>
                </div>
                {scoreTrend.summaryText && (
                  <p className="text-sm text-text-dark">{scoreTrend.summaryText}</p>
                )}
              </div>
            )}

            {/* Top violation types */}
            {topViolationTypes.length > 0 && (
              <div>
                <h4 className="text-sm font-bold text-indigo-600 mb-4">Top Recurring Violation Types</h4>
                <div className="space-y-3">
                  {topViolationTypes.map((violation) => {
                    const badgeColor = SEVERITY_COLORS[violation.impact] ?? '#4F46E5'
                    return (
                      <div
                        key={violation.rule}
                        className="rounded-lg border border-border-light p-4 bg-white"
                      >
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <p className="text-sm font-semibold text-text-dark flex-1">{violation.help}</p>
                          <span
                            className="text-[10px] font-bold text-white px-2.5 py-1 rounded-full uppercase shrink-0"
                            style={{ backgroundColor: badgeColor }}
                          >
                            {violation.impact}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mb-2">{violation.description}</p>
                        <p className="text-xs text-gray-500">
                          Found in {violation.found} violations · {violation.open} still open ·{' '}
                          {violation.fixed} fixed
                        </p>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Recent remediation */}
            {recentRemediation.length > 0 && (
              <div>
                <h4 className="text-sm font-bold text-indigo-600 mb-4">Recent Remediation History</h4>
                <div className="rounded-lg border border-border-light divide-y divide-gray-100">
                  {recentRemediation.slice(0, 8).map((entry) => (
                    <div key={entry.id} className="px-4 py-3 flex items-start gap-3">
                      <Icon icon="mdi:check-circle" className="text-green-500 text-lg shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <p className="text-sm text-text-dark">
                          <span className="font-medium">Issue #{entry.issueNumber}:</span>{' '}
                          {entry.violationType} on{' '}
                          <code className="text-xs bg-gray-100 px-1 rounded">{entry.page}</code>
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Fixed {formatReportDate(entry.fixedAt, true)}
                          {entry.impact && (
                            <span className="ml-2 capitalize">· {entry.impact}</span>
                          )}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-gray-100">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-xs text-gray-500">
                <span className="inline-flex items-center gap-1">
                  <Icon icon="mdi:gavel" className="text-indigo-600" />
                  {totalScans.toLocaleString()} timestamped audit records
                </span>
                {report.generatedAt && (
                  <span>Generated {formatReportDate(report.generatedAt, true)}</span>
                )}
              </div>
              <Button
                variant="secondary"
                icon="mdi:download"
                onClick={handleExportPdf}
                disabled={isExporting || !websiteId}
                size="sm"
              >
                Download PDF
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ComplianceReportSection
